import type { Accent, Mode, Question, QuestionBank, SessionItem, SessionRecord } from '../types';
import { Recorder } from './recorder';
import { examiner } from './examiner';
import { transcribe } from './providers';
import { sttAvailable, loadProviders, effectiveTts } from './keys';
import { computeMetrics, summarize } from './metrics';
import { loadBank, buildDrill } from './questions';
import { saveSession } from './storage';
import { toast } from './toast';

// The session state machine (handoff §6):
//   IDLE → ASKING → CAPTURING → BETWEEN → (more Qs → ASKING) | (none → TRANSCRIBING → REVIEW)
// Pause/abort and restart are first-class. Waveform levels are read straight off
// the recorder each frame by the view — never routed through React state.

export type Phase =
  | 'idle'
  | 'acquiring'
  | 'mic_denied'
  | 'asking'
  | 'capturing'
  | 'between'
  | 'transcribing'
  | 'review';

export interface SessionConfig {
  mode: Mode;
  accent: Accent;
  topics: string[];
  count: number;
  silenceMs: number | null; // VAD auto-advance window (null = manual only)
  maxMs: number | null; // max-answer cutoff (null = none)
  repeatCap: number;
  revealAllowed: boolean;
  betweenMs: number;
}

export interface Snapshot {
  phase: Phase;
  index: number;
  total: number;
  question: Question | null;
  revealed: boolean;
  repeatsLeft: number;
  usingFallbackVoice: boolean;
  notice: string | null;
  micError: string | null;
  record: SessionRecord | null;
  elapsedStartedAt: number | null;
}

const DEFAULTS: Omit<SessionConfig, 'mode' | 'accent' | 'topics'> = {
  count: 12,
  silenceMs: 2500,
  maxMs: 45000,
  repeatCap: 1,
  revealAllowed: true,
  betweenMs: 1200,
};

export function defaultConfig(mode: Mode, accent: Accent, topics: string[], count?: number): SessionConfig {
  return { ...DEFAULTS, mode, accent, topics, ...(count ? { count } : {}) };
}

export class SessionController {
  readonly recorder = new Recorder();
  private snap: Snapshot = {
    phase: 'idle',
    index: 0,
    total: 0,
    question: null,
    revealed: false,
    repeatsLeft: 0,
    usingFallbackVoice: false,
    notice: null,
    micError: null,
    record: null,
    elapsedStartedAt: null,
  };
  private listeners = new Set<() => void>();
  private cfg: SessionConfig | null = null;
  private bank: QuestionBank | null = null;
  private questions: Question[] = [];
  private items: SessionItem[] = [];
  private pending: Promise<void>[] = [];
  private answerSettled = false;
  private aborted = false;
  private ending = false;
  private epoch = 0; // bumped each start(); stale async work checks it (W1)
  private betweenTimer: number | null = null;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = (): Snapshot => this.snap;
  getConfig = (): SessionConfig | null => this.cfg;

  private set(patch: Partial<Snapshot>): void {
    this.snap = { ...this.snap, ...patch };
    this.listeners.forEach((l) => l());
  }

  async start(cfg: SessionConfig): Promise<void> {
    this.aborted = false;
    this.ending = false;
    this.items = [];
    this.pending = [];
    this.cfg = cfg;
    const myEpoch = ++this.epoch;
    this.set({ phase: 'acquiring', micError: null, notice: null, record: null, revealed: false, usingFallbackVoice: false });

    // Acquire the mic once, up front (handoff §7).
    try {
      await this.recorder.acquire();
    } catch (e) {
      const denied = e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError');
      this.set({
        phase: 'mic_denied',
        micError: denied
          ? "Your browser is blocking the microphone. Allow it for this site, then press Retry."
          : 'No microphone was found. Connect one and press Retry.',
      });
      return;
    }
    if (myEpoch !== this.epoch) return;

    // Load the bank + build the question list for the mode. On failure, release
    // the mic (never leave it open) and show an honest error (M3).
    try {
      this.bank = await loadBank();
    } catch {
      this.recorder.release();
      this.set({ phase: 'mic_denied', micError: 'Could not load the question bank. Reload the page and try again.' });
      return;
    }
    if (myEpoch !== this.epoch) return;

    this.questions = this.buildQuestions(cfg);
    if (this.questions.length === 0) {
      this.recorder.release();
      this.set({ phase: 'mic_denied', micError: 'No questions matched — pick at least one topic.' });
      return;
    }
    this.set({ total: cfg.mode === 'endless' ? 0 : this.questions.length, elapsedStartedAt: Date.now() });
    void this.ask(0);
  }

  /** Build the question list for the chosen mode. */
  private buildQuestions(cfg: SessionConfig): Question[] {
    const bank = this.bank!;
    switch (cfg.mode) {
      case 'part3_discussion':
        return buildDrill(bank, 3, cfg.topics, cfg.count);
      case 'endless':
        // Whole Part-1 pool, shuffled; extended on the fly when exhausted.
        return buildDrill(bank, 1, cfg.topics, 9999);
      case 'full_mock': {
        // The real test shape: Part 1 warm-up → one Part 2 cue card → Part 3.
        const p1 = buildDrill(bank, 1, [], 4);
        const p2 = buildDrill(bank, 2, [], 1);
        const p3 = buildDrill(bank, 3, [], 4);
        return [...p1, ...p2, ...p3];
      }
      case 'part1_drill':
      default:
        return buildDrill(bank, 1, cfg.topics, cfg.count);
    }
  }

  private async ask(i: number): Promise<void> {
    if (this.aborted || this.ending) return;
    const myEpoch = this.epoch;
    const cfg = this.cfg!;
    const q = this.questions[i];
    this.answerSettled = false;
    this.set({
      phase: 'asking',
      index: i,
      question: q,
      revealed: false,
      repeatsLeft: cfg.repeatCap,
    });
    const res = await examiner.speak(q.text, cfg.accent);
    if (this.aborted || this.ending || myEpoch !== this.epoch) return; // stale (W1)
    if (res.fallback) this.set({ usingFallbackVoice: true });
    this.beginCapture();
  }

  /** Per-question answer window: derive from the question's suggested seconds so
   * a Part 2 monologue gets ~3.5 min while a Part 1 answer gets ~45 s. */
  private windowsFor(q: Question | null): { silenceMs?: number; maxMs?: number } {
    const cfg = this.cfg!;
    if (!q) return { silenceMs: cfg.silenceMs ?? undefined, maxMs: cfg.maxMs ?? undefined };
    const maxMs = Math.min(240_000, Math.max(20_000, Math.round(q.suggested_answer_seconds * 1800)));
    // A Part 2 long-turn needs a more forgiving silence window so a mid-answer
    // pause doesn't cut it off; short answers keep the brisk default.
    const silenceMs = q.part === 2 ? 4500 : (cfg.silenceMs ?? undefined);
    return { silenceMs, maxMs };
  }

  private beginCapture(): void {
    if (this.aborted || this.ending) return;
    this.set({ phase: 'capturing' });
    const win = this.windowsFor(this.snap.question);
    try {
      this.recorder.start({
        silenceMs: win.silenceMs,
        maxMs: win.maxMs,
        onSilence: () => this.settleAnswer(),
        onMaxReached: () => this.settleAnswer(),
      });
    } catch (e) {
      // Recording couldn't start — degrade honestly rather than wedge in CAPTURING.
      void e;
      this.recorder.release();
      toast('Recording could not start. Reload, or reconnect your microphone.', 'err');
      this.set({
        phase: 'mic_denied',
        micError: 'Recording could not start. Reload the page, or reconnect your microphone, then press Retry.',
      });
    }
  }

  /** User pressed Next / Space, or VAD/cutoff fired. Idempotent per answer. */
  userNext(): void {
    if (this.snap.phase === 'capturing') this.settleAnswer();
  }

  repeat(): void {
    if (this.snap.phase !== 'asking' || this.snap.repeatsLeft <= 0) return;
    this.set({ repeatsLeft: this.snap.repeatsLeft - 1 });
    const q = this.snap.question;
    if (q) void examiner.speak(q.text, this.cfg!.accent);
  }

  toggleReveal(): void {
    if (!this.cfg?.revealAllowed) return;
    if (this.snap.phase === 'asking' || this.snap.phase === 'capturing') {
      this.set({ revealed: !this.snap.revealed });
    }
  }

  /** Stop the recorder, build the item, kick off transcription. No advance. */
  private async captureCurrentAnswer(): Promise<void> {
    const q = this.snap.question!;
    const stopped = await this.recorder.stop();
    const item: SessionItem = {
      question_id: q.id,
      question_text: q.text,
      audio_blob: stopped.blob,
      audio_mime: stopped.mime,
      duration_s: stopped.duration_s,
      transcript: null,
      transcript_status: stopped.spoke ? 'pending' : 'no_speech',
      metrics: null,
    };
    this.items.push(item);
    if (stopped.spoke) this.pending.push(this.transcribeItem(item));
  }

  private async settleAnswer(): Promise<void> {
    if (this.answerSettled || this.snap.phase !== 'capturing') return;
    this.answerSettled = true;
    const cfg = this.cfg!;
    await this.captureCurrentAnswer();
    if (this.aborted || this.ending) return;

    this.set({ phase: 'between' });
    this.betweenTimer = window.setTimeout(() => {
      if (this.aborted || this.ending) return;
      const next = this.snap.index + 1;
      if (next < this.questions.length) {
        void this.ask(next);
      } else if (cfg.mode === 'endless') {
        this.extendEndless();
        void this.ask(next);
      } else {
        void this.finish();
      }
    }, cfg.betweenMs);
  }

  private async transcribeItem(item: SessionItem): Promise<void> {
    if (!sttAvailable()) {
      item.transcript_status = 'skipped_no_key';
      return;
    }
    try {
      const { text, words } = await transcribe(item.audio_blob!);
      if (!text) {
        item.transcript_status = 'no_speech';
        return;
      }
      item.transcript = text;
      item.words = words;
      item.metrics = computeMetrics(words, text, item.duration_s);
      item.transcript_status = 'ok';
    } catch (e) {
      item.transcript_status = 'unavailable';
      // Keep the recording; the review states this item honestly.
      void e;
    }
  }

  /** Endless mode: when the shuffled pool runs dry, append a fresh reshuffle. */
  private extendEndless(): void {
    if (!this.bank || !this.cfg) return;
    const more = buildDrill(this.bank, 1, this.cfg.topics, 9999);
    this.questions = [...this.questions, ...more];
  }

  /** End the session now (finish with what's answered → review). Used by the
   * visible "End" control and as the only way to finish an endless drill. */
  async endSession(): Promise<void> {
    const ph = this.snap.phase;
    if (ph === 'idle' || ph === 'review' || ph === 'transcribing' || this.ending) return;
    this.ending = true;
    if (this.betweenTimer) clearTimeout(this.betweenTimer);
    examiner.stop();
    // Capture an in-progress answer so it isn't lost.
    if (ph === 'capturing' && !this.answerSettled) {
      this.answerSettled = true;
      await this.captureCurrentAnswer();
    }
    if (this.items.length === 0) {
      // Nothing answered yet — just leave, no empty review.
      this.recorder.release();
      this.set({ phase: 'idle', question: null, record: null });
      return;
    }
    await this.finish();
  }

  private async finish(): Promise<void> {
    this.set({ phase: 'transcribing' });
    await Promise.allSettled(this.pending);
    if (this.aborted) return;

    const cfg = this.cfg!;
    const providers = loadProviders();
    const record: SessionRecord = {
      session_id: new Date(this.snap.elapsedStartedAt || Date.now()).toISOString().replace(/[:.]/g, '-'),
      started_at: new Date(this.snap.elapsedStartedAt || Date.now()).toISOString(),
      ended_at: new Date().toISOString(),
      mode: cfg.mode,
      accent: cfg.accent,
      topics: cfg.topics,
      tts_provider: effectiveTts().provider,
      stt_provider: providers.stt,
      items: this.items,
      summary: summarize(this.items),
    };
    try {
      await saveSession(record);
    } catch {
      /* local save best-effort; review still shows the session */
    }
    this.recorder.release();
    this.set({ phase: 'review', record });
  }

  /** Abort/pause: tear the mic down, return to idle without saving. */
  abort(): void {
    this.aborted = true;
    this.ending = false;
    this.epoch++; // invalidate any in-flight ask()
    if (this.betweenTimer) clearTimeout(this.betweenTimer);
    examiner.stop();
    this.recorder.release();
    this.set({ phase: 'idle', question: null, record: null });
  }

  reset(): void {
    this.abort();
    this.set({ phase: 'idle', notice: null, usingFallbackVoice: false });
  }
}

export const controller = new SessionController();
